import React, { useState } from 'react';
import LongXRedlineDesk from '../chart/LongXRedlineDesk';
import RealtimeMultiAssetGrid from '../chart/RealtimeMultiAssetGrid';
import LongXHubModal from '../chart/LongXHubModal';
import AICopilotChat from '../copilot/AICopilotChat';
import SignalScannerGrid from '../scanner/SignalScannerGrid';
import SentimentRadar from '../radar/SentimentRadar';
import AdminPortal from '../admin/AdminPortal';

export default function DashboardShell({ user, token, onLogout }) {
  // Navigation Tabs: 'current_desk' | 'realtime_grid' | 'copilot' | 'scanner' | 'radar' | 'admin'
  const [activeTab, setActiveTab] = useState('current_desk');
  const [currentSymbol, setCurrentSymbol] = useState('XAUUSD');
  const [remainingQueries, setRemainingQueries] = useState(49);

  // Hub Modal state
  const [isHubModalOpen, setIsHubModalOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const username = user?.username || 'TRADER';
  const role = user?.role || 'USER_TRADER';
  const licenseTier = user?.licenseTier || 'PRO';
  const isAdmin = role === 'ADMIN_SUPERUSER';

  // Admin view
  if (activeTab === 'admin') {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
        <AdminPortal onBackToDashboard={() => setActiveTab('current_desk')} />
      </div>
    );
  }

  return (
    <div className="w-full flex-1 min-h-0 overflow-hidden flex flex-col px-2 sm:px-3 py-1.5 gap-1.5 animate-fade-in font-sans text-slate-100 relative">
      {/* 1. UNIFIED SINGLE-ROW TOP NAVIGATION & TELEMETRY BAR (Zero wasted vertical space) */}
      <div className="rounded-xl bg-[#0b0e17] border border-white/10 px-3 py-1.5 flex flex-wrap items-center justify-between gap-2 shadow-xl shrink-0">
        {/* Left: Workspace Tab Switches */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {/* Tab 1: Cửa sổ hiện tại */}
          <button
            type="button"
            onClick={() => setActiveTab('current_desk')}
            className={`px-3 py-1 rounded-lg text-left transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'current_desk'
                ? 'bg-[#151926] text-white border-b-2 border-cyan-400 shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span className="text-xs font-bold tracking-wide">⚡ Cửa sổ hiện tại</span>
            <span className="text-[10px] text-slate-400 font-mono hidden 2xl:inline">(AI Desk)</span>
          </button>

          {/* Tab 2: Cửa sổ giá realtime */}
          <button
            type="button"
            onClick={() => setActiveTab('realtime_grid')}
            className={`px-3 py-1 rounded-lg text-left transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'realtime_grid'
                ? 'bg-[#151926] text-white border-b-2 border-cyan-400 shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span className="text-xs font-bold tracking-wide">📊 Cửa sổ giá realtime</span>
            <span className="text-[10px] text-slate-400 font-mono hidden 2xl:inline">(Multi-Asset)</span>
          </button>

          {/* Supplementary Suites */}
          <div className="hidden lg:flex items-center gap-1 border-l border-white/10 pl-2">
            <button
              type="button"
              onClick={() => setActiveTab('copilot')}
              className={`px-2 py-1 rounded-md text-[11px] font-mono font-semibold transition-all cursor-pointer ${
                activeTab === 'copilot'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/50'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              AI Copilot
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('scanner')}
              className={`px-2 py-1 rounded-md text-[11px] font-mono font-semibold transition-all cursor-pointer ${
                activeTab === 'scanner'
                  ? 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/50'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Signal Scanner
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('radar')}
              className={`px-2 py-1 rounded-md text-[11px] font-mono font-semibold transition-all cursor-pointer ${
                activeTab === 'radar'
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-400/50'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Sentiment Radar
            </button>
          </div>
        </div>

        {/* Center: Operator Telemetry (Compact inline) */}
        <div className="flex items-center space-x-2 text-[10.5px] font-mono text-slate-400 bg-white/[0.02] px-2.5 py-1 rounded-lg border border-white/5">
          <span>OPERATOR //</span>
          <span className="text-cyan-400 font-bold">{username}</span>
          <span className="text-slate-600">|</span>
          <span className="text-amber-300 font-semibold">{licenseTier} TIER</span>
          <span className="text-slate-600 hidden md:inline">|</span>
          <span className="text-slate-400 hidden md:inline">Core Intelligence Engines</span>
        </div>

        {/* Right: Hub Button, Quota, Admin & Logout */}
        <div className="flex items-center gap-2 text-xs font-mono">
          {/* Docked Hub Button (No longer floating over chart bottom-right) */}
          <button
            type="button"
            onClick={() => setIsHubModalOpen(true)}
            className="px-2.5 py-1 rounded-lg bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-500 hover:to-sky-400 text-white font-bold font-mono text-[11px] flex items-center gap-1 transition-all shadow-sm cursor-pointer border border-sky-400/40"
          >
            <span>↗</span>
            <span>Mở Long X hub</span>
          </button>

          <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/40 text-amber-300 font-extrabold tracking-wider text-[10px]">
            PRO
          </span>

          <div className="flex items-center gap-1 text-slate-300 font-bold bg-[#141824] px-2 py-1 rounded-lg border border-white/5 text-[11px]">
            <span>⌛</span>
            <span>Lượt: <strong className="text-white">{remainingQueries}</strong></span>
          </div>

          {isAdmin && (
            <button
              type="button"
              onClick={() => setActiveTab('admin')}
              className="px-2 py-1 rounded-lg bg-fuchsia-950/40 border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/20 hover:text-white transition-all text-[11px] font-semibold cursor-pointer"
            >
              Admin Panel
            </button>
          )}

          <button
            type="button"
            onClick={onLogout}
            aria-label="DISCONNECT"
            className="px-2.5 py-1 rounded-lg bg-[#141824] hover:bg-rose-500/20 text-slate-300 hover:text-rose-300 border border-white/10 hover:border-rose-500/30 transition-all cursor-pointer flex items-center gap-1 font-mono text-[11px]"
          >
            <span>⎋</span>
            <span>Đăng xuất</span>
            <span className="sr-only">DISCONNECT</span>
          </button>
        </div>
      </div>

      {/* 2. MAIN ACTIVE VIEW ROUTING (Fills 100% of remaining height without page scroll) */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col w-full">
        {activeTab === 'current_desk' && (
          <LongXRedlineDesk
            symbol={currentSymbol}
            onSymbolChange={setCurrentSymbol}
            remainingQueries={remainingQueries}
            onConsumeQuery={() => setRemainingQueries(q => Math.max(0, q - 1))}
          />
        )}

        {activeTab === 'realtime_grid' && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <RealtimeMultiAssetGrid
              onSelectAsset={(sym) => {
                setCurrentSymbol(sym);
                setActiveTab('current_desk');
              }}
            />
          </div>
        )}

        {activeTab === 'copilot' && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <AICopilotChat />
          </div>
        )}

        {activeTab === 'scanner' && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <SignalScannerGrid />
          </div>
        )}

        {activeTab === 'radar' && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <SentimentRadar />
          </div>
        )}
      </div>

      {/* 3. MODAL POPUP FOR LONGX HUB */}
      <LongXHubModal
        isOpen={isHubModalOpen}
        onClose={() => setIsHubModalOpen(false)}
        onOpenHub={() => {
          setIsHubModalOpen(false);
          setActiveTab('realtime_grid');
        }}
        dontShowAgain={dontShowAgain}
        setDontShowAgain={setDontShowAgain}
      />
    </div>
  );
}

export { DashboardShell };
